import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationThreadResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { useRequiredContext } from "@redotech/react-util/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ConversationActionToolbar } from "../components/conversation-action-toolbar";
import { ConversationCardList } from "../components/conversation-card-list";
import { ConversationDetailsPanel } from "../components/conversation-details-panel";
import { ConversationHeader } from "../components/conversation-header";
import { EmailThreadView } from "../components/email-thread-view";
import { MessageInput } from "../components/message-input";
import { UnsupportedThreadView } from "../components/unsupported-thread-view";
import { useConversationCardList } from "../hooks/use-conversation-card-list";
import { useConversationView } from "../hooks/use-conversation-view";
import { useConversationViewLayout } from "../hooks/use-conversation-view-layout";

interface ConversationViewProps {
  conversationId: ConversationId;
}

/**
 * Conversation view component that displays conversation data.
 * Renders threads using channel-appropriate components.
 */
export const ConversationView = memo(function ConversationView({
  conversationId,
}: ConversationViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const team = useRequiredContext(TeamContext);

  const { conversation, threads, isLoading, customerName, latestMessageTime } =
    useConversationView(conversationId);

  const { leftPanelOpen, toggleLeftPanel, rightPanelOpen, toggleRightPanel } =
    useConversationViewLayout();

  const {
    conversations,
    isLoading: isLoadingConversations,
    isFetchingMore,
    isRefreshing,
    hasNextPage,
    selectConversation,
    refresh,
    loadMore,
    isConversationSelected,
  } = useConversationCardList();

  if (isLoading) {
    return (
      <Flex align="center" justify="center" p="xl">
        <RedoSpinner />
      </Flex>
    );
  }

  if (!conversation) {
    return (
      <Flex align="center" justify="center" p="xl">
        <Text>Conversation not found</Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" height="full" overflow="hidden" w="full">
      <ConversationHeader
        actionToolbar={
          <ConversationActionToolbar
            conversation={conversation}
            conversationId={conversationId}
          />
        }
        customerName={customerName}
        latestMessageTime={latestMessageTime}
        leftPanelOpen={leftPanelOpen}
        onBack={() =>
          navigate(`/stores/${team._id}/support-v2${location.search}`)
        }
        onToggleLeftPanel={toggleLeftPanel}
        onToggleRightPanel={toggleRightPanel}
        rightPanelOpen={rightPanelOpen}
      />
      <Flex flex={1} style={{ minHeight: 0 }}>
        {/* Left panel - conversation ticket list */}
        {leftPanelOpen && (
          <Flex
            borderColor="secondary"
            borderRightWidth="1px"
            borderStyle="solid"
            dir="column"
            style={{ flex: "0 0 320px" }}
          >
            <ConversationCardList
              conversations={conversations}
              hasMore={hasNextPage}
              isCardSelected={isConversationSelected}
              isFetchingMore={isFetchingMore}
              isLoading={isLoadingConversations}
              isRefreshing={isRefreshing}
              onCardClick={selectConversation}
              onCollapse={toggleLeftPanel}
              onLoadMore={loadMore}
              refresh={refresh}
            />
          </Flex>
        )}

        {/* Center content - conversation threads and message input */}
        <Flex bgColor="secondary" dir="column" style={{ flex: 1, minWidth: 0 }}>
          {/* Scrollable thread area */}
          <Flex dir="column" grow={1} overflowY="auto" p="xl">
            <Flex dir="column" gap="xl" grow={1}>
              {threads.length === 0 ? (
                <Flex align="center" grow={1} justify="center">
                  <Text fontSize="md" textColor="secondary">
                    No messages in this thread
                  </Text>
                </Flex>
              ) : (
                threads.map((thread) => (
                  <ThreadRenderer key={thread.id} thread={thread} />
                ))
              )}
            </Flex>
          </Flex>
          {/* Message input at bottom */}
          <Flex
            bgColor="primary"
            borderColor="secondary"
            borderStyle="solid"
            borderTopWidth="1px"
            px="xl"
          >
            <MessageInput />
          </Flex>
        </Flex>

        {/* Right panel - conversation details */}
        {rightPanelOpen && (
          <Flex
            bgColor="primary"
            borderColor="secondary"
            borderLeftWidth="1px"
            borderStyle="solid"
            dir="column"
            style={{ flex: "0 0 280px" }}
          >
            <ConversationDetailsPanel conversation={conversation} />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

interface ThreadRendererProps {
  thread: ConversationThreadResponse;
}

/**
 * Renders a thread using the appropriate component based on integration type.
 * This switch is exhaustive - TypeScript will error if a new integration type
 * is added without handling it here.
 */
const ThreadRenderer = memo(function ThreadRenderer({
  thread,
}: ThreadRendererProps) {
  switch (thread.integrationType) {
    case "gmail":
    case "outlook":
      return <EmailThreadView thread={thread} />;
    case "facebook":
      return <UnsupportedThreadView channel="facebook" />;
    case "instagram":
      return <UnsupportedThreadView channel="instagram" />;
    case "tiktok":
      return <UnsupportedThreadView channel="tiktok" />;
    case "attentive":
      return <UnsupportedThreadView channel="attentive" />;
    case "postscript":
      return <UnsupportedThreadView channel="postscript" />;
    case "redoChatAi":
    case "redoChatFlow":
    case "redoChatLive":
      return <UnsupportedThreadView channel="redoChat" />;
  }
  // Exhaustive check - TypeScript will error here if a new integration type is added
  const _exhaustiveCheck: never = thread.integrationType;
  return _exhaustiveCheck;
});
